import { CollapseButton } from "@msuite/ui/button";
import { Divider } from "@msuite/ui/divider";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { CompareValue } from "@msuite/ui/misc";
import { cn } from "@msuite/ui/utils";
import { getAuditUpdateDiff } from "@msuite/utils/audit";
import type { TranslationKey } from "@msuite/utils/locales";
import type { Tables } from "@msuite/utils/types";
import React, { Fragment, useMemo } from "react";
import UserCard from "@/components/cards/user-card";

/** Props Interface */
interface ProjectTimelineAuditUpdateItemProps {
	item: Tables<"projectsAudit">;
}

export function ProjectTimelineAuditUpdateItem({ item }: ProjectTimelineAuditUpdateItemProps) {
	/** Hooks */
	const { t } = useTranslation();
	const disclosure = useDisclosure();

	const diff = useMemo(() => {
		return getAuditUpdateDiff<Tables<"projects">>(item.oldData, item.newData);
	}, [item]);
	const hasDiff = diff.length > 0;
	const isSystemDiff = !item.changedBy;
	const label: TranslationKey = isSystemDiff
		? "projects._system_updated_this_project"
		: hasDiff
			? "projects._updated_this_project"
			: "projects._updated_this_project_without_changes";

	/** Render */
	return (
		<div className="v-stack">
			<div className="h-stack">
				<CollapseButton
					className="-m-2 mr-1"
					isOpen={disclosure.isOpen}
					onToggle={disclosure.onToggle}
					isDisabled={!hasDiff}
				/>
				{!isSystemDiff ? (
					<Fragment>
						<UserCard
							userId={item?.changedBy as string}
							variant="plain"
							fontSize="md"
						/>
						<p className="-ml-1">{t(label)}.</p>
					</Fragment>
				) : (
					<p>{t(label)}.</p>
				)}
			</div>
			{disclosure.isOpen && hasDiff && (
				<React.Fragment>
					<Divider variant="card" />
					<div className="overflow-hidden max-w-full">
						<div
							className={cn(
								"items-center gap-x-3 gap-y-1 grid grid-cols-4 overflow-hidden text-xs",
							)}
							style={{
								gridTemplateColumns: "max-content 1fr max-content 1fr",
							}}
						>
							{diff.map((diff) => (
								<React.Fragment key={diff.key}>
									<p>{diff.key}</p>
									<div className="overflow-hidden w-full">
										<CompareValue
											width="full"
											variant="old"
										>
											{diff.oldValue}
										</CompareValue>
									</div>
									<Icon
										name="ArrowRight"
										className="stroke-primaryGray-500"
									/>
									<div className="overflow-hidden w-full">
										<CompareValue
											width="full"
											variant="new"
										>
											{diff.newValue}
										</CompareValue>
									</div>
								</React.Fragment>
							))}
						</div>
					</div>
				</React.Fragment>
			)}
		</div>
	);
}
